import React from 'react';

/**
 * Regular expression to detect URLs in text
 * Matches http://, https://, and www. patterns
 */
const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

/**
 * More comprehensive URL regex that also catches URLs without protocol
 */
const COMPREHENSIVE_URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/gi;

/**
 * Validate if a string is a valid URL
 */
export const isValidUrl = (string: string): boolean => {
  try {
    // Add protocol if missing
    const urlString = string.startsWith('http') ? string : `https://${string}`;
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Normalize URL by adding protocol if missing
 */
export const normalizeUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

/**
 * Extract all URLs from text
 */
export const extractUrls = (text: string): string[] => {
  const matches = text.match(URL_REGEX);
  return matches ? matches.filter(isValidUrl) : [];
};

/**
 * Convert URLs in text to clickable links
 * Returns JSX elements with links that open in new tabs
 */
export const renderTextWithLinks = (text: string): React.ReactNode => {
  if (!text) return text;

  // Split text by URLs while keeping the URLs
  const parts = text.split(URL_REGEX);
  
  return parts.map((part, index) => {
    // Check if this part is a URL
    if (URL_REGEX.test(part)) {
      const normalizedUrl = normalizeUrl(part);
      
      return (
        <a
          key={index}
          href={normalizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline break-all"
          onClick={(e) => {
            // Additional validation before opening
            if (!isValidUrl(part)) {
              e.preventDefault();
              console.warn('Invalid URL clicked:', part);
            }
          }}
        >
          {part}
        </a>
      );
    }
    
    // Regular text
    return part;
  });
};

/**
 * Convert URLs in text to clickable links (string version for simple cases)
 * Returns HTML string with anchor tags
 */
export const convertTextToLinksHtml = (text: string): string => {
  if (!text) return text;

  return text.replace(URL_REGEX, (url) => {
    if (!isValidUrl(url)) return url;
    
    const normalizedUrl = normalizeUrl(url);
    return `<a href="${normalizedUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline break-all">${url}</a>`;
  });
};

/**
 * Component for rendering text with clickable links
 */
interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export const LinkifiedText: React.FC<LinkifiedTextProps> = ({ text, className = '' }) => {
  return (
    <span className={className}>
      {renderTextWithLinks(text)}
    </span>
  );
};

/**
 * Hook for processing text with links
 */
export const useTextWithLinks = (text: string) => {
  return React.useMemo(() => {
    return {
      hasLinks: URL_REGEX.test(text),
      urls: extractUrls(text),
      renderedText: renderTextWithLinks(text)
    };
  }, [text]);
};

/**
 * Utility to check if text contains any URLs
 */
export const containsUrls = (text: string): boolean => {
  return URL_REGEX.test(text);
};

/**
 * Utility to get URL preview info (domain, etc.)
 */
export const getUrlInfo = (url: string) => {
  try {
    const normalizedUrl = normalizeUrl(url);
    const urlObj = new URL(normalizedUrl);
    
    return {
      domain: urlObj.hostname,
      protocol: urlObj.protocol,
      pathname: urlObj.pathname,
      isSecure: urlObj.protocol === 'https:',
      displayUrl: url // Original URL for display
    };
  } catch {
    return null;
  }
};

/**
 * Component for displaying URL preview
 */
interface UrlPreviewProps {
  url: string;
  showDomain?: boolean;
  showSecurityIcon?: boolean;
}

export const UrlPreview: React.FC<UrlPreviewProps> = ({ 
  url, 
  showDomain = true, 
  showSecurityIcon = true 
}) => {
  const urlInfo = getUrlInfo(url);
  
  if (!urlInfo) return null;

  return (
    <div className="inline-flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
      {showSecurityIcon && (
        <span className={urlInfo.isSecure ? 'text-green-500' : 'text-yellow-500'}>
          {urlInfo.isSecure ? '🔒' : '⚠️'}
        </span>
      )}
      {showDomain && <span>{urlInfo.domain}</span>}
    </div>
  );
};